import { useCallback, useEffect, useRef, useState } from 'react';

import { debounce, noop } from 'lodash';
import { Notification } from '@bringg/react-components';
import { useHasFeatureFlag } from '@bringg-frontend/hooks';

import { useStores } from 'bringg-web/recipes';
import { useCrossApp } from 'bringg-web/services/cross-application/cross-application';
import { EmployeeListInMapTab } from 'bringg-web/services/cross-application/cross-application.actions';
import type { DisplayedEmployees } from '../employee-list';

export const useStoreData = () => {
	const { authStore, userTypeStore, vehiclesStore } = useStores();

	const [vehiclesAreLoading, setVehiclesAreLoading] = useState(false);
	const isMountedRef = useRef(true);

	const isOnDemandVehiclesLoading = useHasFeatureFlag('on_demand_vehicles_loading');
	const showVehicleName = useHasFeatureFlag('show_vehicle_name_in_employee_list');

	// There are two events published from AJS for each Driver List change
	// We have to debounce the callback to avoid possible API calls duplication
	const loadVehiclesForFilteredEmployees = useCallback(
		debounce(async (newDisplayed: DisplayedEmployees) => {
			const vehicleIds = [...new Set((newDisplayed?.filtered || []).map(e => e.vehicle_id).filter(Boolean))];

			if (!isMountedRef.current) return;

			try {
				setVehiclesAreLoading(true);
				await vehiclesStore.loadMany(vehicleIds, { useCache: true });
			} catch {
				Notification.error('Failed to Load Many Vehicles');
			} finally {
				if (isMountedRef.current) {
					setVehiclesAreLoading(false);
				}
			}
		}, 100),
		[vehiclesStore, setVehiclesAreLoading]
	);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			loadVehiclesForFilteredEmployees.cancel();
		};
	}, [loadVehiclesForFilteredEmployees]);

	useCrossApp(
		EmployeeListInMapTab.EMPLOYEE_LIST_EMPLOYEES_UPDATED,
		showVehicleName && isOnDemandVehiclesLoading ? loadVehiclesForFilteredEmployees : noop,
		authStore.crossAppTransport
	);

	useEffect(() => {
		void userTypeStore.fetchAll();
		if (!isOnDemandVehiclesLoading) {
			void vehiclesStore.loadAll();
		}
	}, []);

	return {
		userTypesReady: userTypeStore.isFetched,
		vehiclesAreLoading
	};
};
